import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { ReferralStage, ReferralSource, ReferralStatus } from '~/types/transition';
import { useTransitionContext } from '~/hooks/transition/useTransitionContext';
import { useTransitionAttention } from '~/providers/TransitionAttentionProvider';
import PipelineColumn from './PipelineColumn';

const STAGES: ReferralStage[] = [
  ReferralStage.PROCESSING,
  ReferralStage.DECISION,
  ReferralStage.EXECUTION,
];

interface PipelineBoardProps {
  filterSource: ReferralSource | 'ALL';
  filterStage: ReferralStage | 'ALL';
}

const PipelineBoard: React.FC<PipelineBoardProps> = ({ filterSource, filterStage }) => {
  const { referrals } = useTransitionContext();
  const { isUnseen } = useTransitionAttention();

  const filtered = useMemo(() => {
    const result = referrals.filter((r) => {
      if (r.status === ReferralStatus.DECLINED || r.status === ReferralStatus.AUTO_DECLINED) return false;
      if (filterSource !== 'ALL' && r.source !== filterSource) return false;
      if (filterStage !== 'ALL' && r.stage !== filterStage) return false;
      return true;
    });
    return [...result].sort((a, b) => {
      const aUnseen = isUnseen(a.id) ? 0 : 1;
      const bUnseen = isUnseen(b.id) ? 0 : 1;
      if (aUnseen !== bUnseen) return aUnseen - bUnseen;
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  }, [referrals, filterSource, filterStage, isUnseen]);

  const byStage = STAGES.reduce<Record<ReferralStage, typeof referrals>>(
    (acc, stage) => {
      acc[stage] = filtered.filter((r) => r.stage === stage);
      return acc;
    },
    {} as Record<ReferralStage, typeof referrals>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        alignItems: 'flex-start',
        overflowX: 'auto',
        flex: 1,
        minHeight: 0,
        pb: 2,
      }}
    >
      {STAGES.map((stage) => (
        <PipelineColumn key={stage} stage={stage} referrals={byStage[stage]} />
      ))}
    </Box>
  );
};

export default PipelineBoard;
